import React, { useState, useRef } from "react"
import {DfLogo, MailIcon, PasswordIcon, EyeIcon, EyeOffIcon, UserIcon, BuildingIcon} from "assets/icons.jsx";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiUrl } from "config/api";
import { clearAuthState } from "utils/auth.js";
import i18n from "../../i18n";

// --- Main App Component ---

export default function Register() {
    const nav = useNavigate();
    const { t } = useTranslation();

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [company, setCompany] = useState("");
    const [email, setEmail] = useState("");
    const [preferredLanguage, setPreferredLanguage] = useState(() => {
      const lng = String(i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
      if (lng.startsWith("de")) return "de";
      if (lng.startsWith("zh")) return "zh";
      return "en";
    });

    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");
    const [pwVisible1, setPwVisible1] = useState(false);
    const [pwVisible2, setPwVisible2] = useState(false);
    const passwordsMismatch = password2.length > 0 && password !== password2;

    const [firstNameErr, setFirstNameErr] = useState("");
    const [lastNameErr, setLastNameErr] = useState("");
    const [companyErr, setCompanyErr] = useState("");
    const [emailErr, setEmailErr] = useState("");
    const [passwordErr, setPasswordErr] = useState("");
    const [password2Err, setPassword2Err] = useState("");

    // Opening the register view should always clear any previous auth state/tokens
    React.useEffect(() => {
      clearAuthState();
    }, []);

    // Helper function to compute password strength
    const getPasswordStrength = (pwd) => {
      if (!pwd) return { score: 0, label: "", color: "transparent" };
      let score = 0;
      if (pwd.length >= 8) score++;
      if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
      if (/[0-9]/.test(pwd)) score++;
      if (/[^A-Za-z0-9]/.test(pwd)) score++;
      // Cap score between 0..4
      score = Math.max(0, Math.min(score, 4));
      const palette = ["#ff4d4f", "#ff4d4f", "#f39c12", "#f1c40f", "#2ecc71"]; // red -> yellow -> green
      const labels  = [
        t("auth.register.strength.veryWeak"),
        t("auth.register.strength.weak"),
        t("auth.register.strength.fair"),
        t("auth.register.strength.strong"),
        t("auth.register.strength.veryStrong"),
      ];
      return { score, label: labels[score], color: palette[score] };
    };

    const handleRegister = async (e) => {
      e.preventDefault();

      // Required field validation
      const missingFirst = !firstName || !firstName.trim();
      const missingLast = !lastName || !lastName.trim();
      const missingCompany = !company || !company.trim();
      const missingEmail = !email || !email.trim();
      const missingPw1 = !password || !password.trim();
      const missingPw2 = !password2 || !password2.trim();

      setFirstNameErr(missingFirst ? t("auth.register.error.firstNameRequired") : '');
      setLastNameErr(missingLast ? t("auth.register.error.lastNameRequired") : '');
      setCompanyErr(missingCompany ? t("auth.register.error.companyRequired") : '');
      setEmailErr(missingEmail ? t("auth.register.error.emailRequired") : '');
      setPasswordErr(missingPw1 ? t("auth.register.error.passwordRequired") : '');
      setPassword2Err(missingPw2 ? t("auth.register.error.passwordRepeatRequired") : '');

      if (missingFirst || missingLast || missingCompany || missingEmail || missingPw1 || missingPw2) {
        return; // stop and show inline errors
      }

      if (passwordsMismatch) {
        setPassword2Err(t("auth.register.error.passwordMismatch"));
        return;
      }

      try {
        const res = await fetch(apiUrl("/api/register/"), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            company: company,
                email: email,
                password: password,
                preferred_language: preferredLanguage,
              }),
        });
        if (!res.ok) {
          const msg = await res.text();
          throw new Error(msg || `HTTP ${res.status}`);
        }
        try {
          localStorage.setItem("df_language", preferredLanguage);
          i18n.changeLanguage(preferredLanguage);
        } catch {}
        // On success, return to login and show a one-time success popup.
        nav('/login', {
          replace: true,
          state: {
            registrationSuccess: true,
            registrationMessage: t("auth.register.successMessage"),
            registrationPreferredLanguage: preferredLanguage,
          },
        });
      } catch (err) {
        alert(`${t("auth.register.error.failedPrefix")}${err?.message || err}`);
      }
    };

    // The background video URL
    const backgroundVideoUrl = "/background_new.mp4";
    const pwStrength = getPasswordStrength(password);

    return (
      <div className="login-page" style={{ position: 'relative', width: '100%', minHeight: '100vh', overflow: 'hidden', fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial' }}>
        {/* Background video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
        >
          <source src={backgroundVideoUrl} type="video/mp4" />
        </video>

        {/* Overlay blur */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(37,37,37,0.25)', backdropFilter: 'blur(3.7px)', WebkitBackdropFilter: 'blur(3.7px)', zIndex: 1 }} />

        {/* Center Glass Card */}
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -55%)', width: '92%', maxWidth: 507, borderRadius: 25, overflow: 'hidden', zIndex: 2, boxShadow: '0px 0px 3px 0px #f2f2f2 inset, 0px 0px 3px 0px #ffffff80 inset, -1px -1px 0.5px -1px #ffffff inset, 1px 1px 0.5px -1px #ffffff inset, -1px -1px 0px -0.5px #262626 inset, 1px 1px 0px -0.5px #333333 inset', background: 'rgba(0, 0, 0, 0.17)', backgroundBlendMode: 'screen, luminosity, darken' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,8,8,0.03)', backdropFilter: 'blur(3px)' }} />
          <div style={{ position: 'relative', padding: 24 }}>
            {/* Brand Row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 232 }}>
                <DfLogo style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleRegister}>
              {/* First name */}
              <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: firstNameErr ? '0 0 0 1px #ff4d4f inset, -2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)' : '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 5, paddingRight: 16, gap: 12 }}>
                  <UserIcon />
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    placeholder={t("auth.register.firstName.placeholder")}
                    autoComplete="off"
                    spellCheck={false}
                    value={firstName}
                    onChange={(e) => { const v = e.target.value; setFirstName(v); if (firstNameErr && v && v.trim()) setFirstNameErr(''); }}
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14, fontStyle: 'italic' }}
                  />
                </div>
              </div>
              {firstNameErr && (<div style={{ color: '#ff4d4f', fontSize: 12, marginBottom: 8 }}>{firstNameErr}</div>)}

              {/* Last name */}
              <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: lastNameErr ? '0 0 0 1px #ff4d4f inset, -2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)' : '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 5, paddingRight: 16, gap: 12 }}>
                  <UserIcon />
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    placeholder={t("auth.register.lastName.placeholder")}
                    autoComplete="off"
                    spellCheck={false}
                    value={lastName}
                    onChange={(e) => { const v = e.target.value; setLastName(v); if (lastNameErr && v && v.trim()) setLastNameErr(''); }}
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14, fontStyle: 'italic' }}
                  />
                </div>
              </div>
              {lastNameErr && (<div style={{ color: '#ff4d4f', fontSize: 12, marginBottom: 8 }}>{lastNameErr}</div>)}

              {/* Company */}
              <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: companyErr ? '0 0 0 1px #ff4d4f inset, -2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)' : '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 5, paddingRight: 16, gap: 12 }}>
                  <BuildingIcon />
                  <input
                    type="text"
                    name="company"
                    id="company"
                    placeholder={t("auth.register.company.placeholder")}
                    autoComplete="off"
                    spellCheck={false}
                    value={company}
                    onChange={(e) => { const v = e.target.value; setCompany(v); if (companyErr && v && v.trim()) setCompanyErr(''); }}
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14, fontStyle: 'italic' }}
                  />
                </div>
              </div>
              {companyErr && (<div style={{ color: '#ff4d4f', fontSize: 12, marginBottom: 8 }}>{companyErr}</div>)}

              {/* Preferred language */}
              <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 16, paddingRight: 16 }}>
                  <select
                    value={preferredLanguage}
                    onChange={(e) => {
                      const next = e.target.value;
                      setPreferredLanguage(next === "de" ? "de" : next === "zh" ? "zh" : "en");
                    }}
                    style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14, fontStyle: 'italic' }}
                  >
                    <option value="en" style={{ color: "#111" }}>{t("auth.register.language.en")}</option>
                    <option value="de" style={{ color: "#111" }}>{t("auth.register.language.de")}</option>
                    <option value="zh" style={{ color: "#111" }}>{t("auth.register.language.zh")}</option>
                  </select>
                </div>
              </div>

              {/* Password */}
              <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: (passwordErr || passwordsMismatch) ? '0 0 0 1px #ff4d4f inset, -2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)' : '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 5, paddingRight: 16, gap: 12 }}>
                  <PasswordIcon />
                  <input
                    type={pwVisible1 ? 'text' : 'password'}
                    name="password"
                    id="password"
                    placeholder={t("auth.register.password.placeholder")}
                    autoComplete="new-password"
                    spellCheck={false}
                    value={password}
                    onChange={(e) => { const v = e.target.value; setPassword(v); if (passwordErr && v && v.trim()) setPasswordErr(''); if (password2) { /* re-eval mismatch hint live */ } if (password2Err) setPassword2Err(''); }}
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14 }}
                  />
                  <button type="button" onClick={() => setPwVisible1(v => !v)} style={{ background: 'transparent', border: 'none', outline: 'none', cursor: 'pointer', padding: 4 }}>
                    {pwVisible1 ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              {passwordErr && (<div style={{ color: '#ff4d4f', fontSize: 12, marginBottom: 8 }}>{passwordErr}</div>)}

              {password && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(pwStrength.score / 4) * 100}%`,
                      height: '100%',
                      borderRadius: 4,
                      background: pwStrength.color,
                      transition: 'width 200ms ease'
                    }} />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>
                    {t("auth.register.password.strengthLabel")}: {pwStrength.label}
                  </div>
                </div>
              )}

              {/* Repeat Password */}
              <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: (password2Err || passwordsMismatch) ? '0 0 0 1px #ff4d4f inset, -2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)' : '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 5, paddingRight: 16, gap: 12 }}>
                  <PasswordIcon />
                  <input
                    type={pwVisible2 ? 'text' : 'password'}
                    name="password2"
                    id="password2"
                    placeholder={t("auth.register.passwordRepeat.placeholder")}
                    autoComplete="new-password"
                    spellCheck={false}
                    value={password2}
                    onChange={(e) => { const v = e.target.value; setPassword2(v); if (password2Err && v && v.trim()) setPassword2Err(''); }}
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14 }}
                  />
                  <button type="button" onClick={() => setPwVisible2(v => !v)} style={{ background: 'transparent', border: 'none', outline: 'none', cursor: 'pointer', padding: 4 }}>
                    {pwVisible2 ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              {password2Err && !passwordsMismatch && (<div style={{ color: '#ff4d4f', fontSize: 12, marginBottom: 8 }}>{password2Err}</div>)}

              {passwordsMismatch && (<div style={{ color: '#ff4d4f', fontSize: 12, marginBottom: 8 }}>{t("auth.register.error.passwordMismatch")}</div>)}

              {/* Email */}
              <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: emailErr ? '0 0 0 1px #ff4d4f inset, -2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)' : '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 5, paddingRight: 16, gap: 12 }}>
                  <MailIcon />
                  <input
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t("auth.register.email.placeholder")}
                    autoComplete="off"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => { const v = e.target.value; setEmail(v); if (emailErr && v && v.trim()) setEmailErr(''); }}
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14, fontStyle: 'italic' }}
                  />
                </div>
              </div>

              {/* Register Button */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                <button
                  type="submit"
                  style={{
                display: 'flex',
                width: 140,
                height: 36,
                padding: '10px 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 10,
                flexShrink: 0,
                borderRadius: 5,
                background: 'transparent',
                backgroundBlendMode: 'plus-lighter, luminosity, darken',
                boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)',
                color: '#FFF',
                letterSpacing: 1,
                fontWeight: 500,
                border: 'none'
              }}
                >
                  {t("auth.register.button")}
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => nav('/login')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255,255,255,0.6)', // more transparent
                    textDecoration: 'none',        // no underline
                    fontStyle: 'italic',           // italic
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  {t("auth.register.loginPrompt")}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Bottom content */}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 1, display: "none" }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 16px 24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
            <div style={{ width: '100%', maxWidth: 577, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 200 }}>
                <DfLogo style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24, color: '#F0F0F0', fontSize: 14, fontWeight: 600 }}>
                <div>{t("footer.nav.marketInsights")}</div>
                <div>{t("footer.nav.dataSolutions")}</div>
                <div>{t("footer.nav.analyticsTools")}</div>
                <div>{t("footer.nav.clientSupport")}</div>
                <div>{t("footer.nav.contactUs")}</div>
              </div>
            </div>
            <div style={{ width: '100%' }}>
              <div style={{ height: 0, borderTop: '1px solid rgba(240,240,240,0.9)' }} />
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24, color: '#F0F0F0', fontSize: 14, textDecoration: 'underline' }}>
                <div>{t("footer.legal.privacyPolicy")}</div>
                <div>{t("footer.legal.termsOfService")}</div>
                <div>{t("footer.legal.cookiesSettings")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
}
